// @ts-nocheck
import { Box, Typography, IconButton, Tooltip, Badge } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import ChatIcon from '@mui/icons-material/Chat';
import { useSelector } from 'react-redux';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';

import {
  checkAttributeIsRequired,
  formatAttributeValue,
  isValueEmpty,
} from '../../../../utils/Utils';
import useTextOverflow from '../../../../hooks/useTextOverFlow';
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
  NOOP,
} from '../../../../const/CommonConst';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import OverrideRationaleTooltip from '../../../../components/Common/OverrideRationaleTooltip';

/**
 * Enhanced FieldInfo component with edit functionality
 * @param {Object} props - Component props
 * @param {string} props.label - The field label
 * @param {any} props.value - The field value
 * @param {Function} props.onEdit - Function to handle edit click
 * @param {Object} props.attribute - The attribute object with metadata
 * @returns {React.ReactElement} The field info component
 */
const FieldInfo = ({
  label,
  value,
  onEdit = NOOP,
  attribute,
  mode = 'edit',
  hasDraftChanges = false,
  isUserAuthorizedForUpdateAttribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  handleOpenDerivedModal = NOOP,
  overrideData,
  entityId = null,
  entityDetailsFormikForm = EMPTY_OBJECT,
  usersList = EMPTY_ARRAY,
  groupList = EMPTY_ARRAY,
  groupDetails = EMPTY_OBJECT,
  entityType = null,
  onHistoryClick = NOOP,
  associationTypes = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
}) => {
  // Use the centralized utility function to format the value
  const displayValue = formatAttributeValue(
    value,
    attribute,
    hasDraftChanges,
    entityId,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityType,
    associationTypes,
    false,
    false,
    entityData
  );
  const { ref: labelRef, isOverflowed: isLabelOverflowed } =
    useTextOverflow(label);

  const { ref: valueRef, isOverflowed: isValueOverflowed } =
    useTextOverflow(displayValue);
  const { frontendConfig, pluginConfig } = useSelector(
    (state) => state?.tenant
  );
  const { permissions } = useSelector((state) => state?.users || {});
  // Check if field is required based on yup_schema
  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);
  const matchedOverride = useMemo(() => {
    return overrideData?.find?.((item) => item?.attributeId === attribute?.id);
  }, [overrideData, attribute?.id]);

  // Determine if the attribute is editable based on its properties
  // and whether it is excluded from editing
  const isEditable = useMemo(() => {
    return attribute?.is_editable && mode === 'edit';
  }, [attribute]);

  const isDerived = useMemo(() => {
    return attribute?.is_derived && mode === 'edit';
  }, [attribute]);

  const isEditEnabled = useMemo(() => {
    if (
      attribute?.name?.toLowerCase() === 'assigneduser' &&
      pluginConfig?.HDFCUAM
    ) {
      return permissions?.is_super_admin || groupDetails?.isGroupAdmin;
    }
    if (attribute?.data_type === 'InventoryView') {
      return false; // InventoryView attributes are not editable in the single edit mode as per the current requirements
    }
    return isEditable && !isDerived && !frontendConfig?.hideSingleEditMode;
  }, [
    isEditable,
    isDerived,
    frontendConfig?.hideSingleEditMode,
    attribute?.name,
    permissions?.is_super_admin,
    groupDetails?.isGroupAdmin,
    pluginConfig?.HDFCUAM,
  ]);
  const isUserEditEnabled = useMemo(() => {
    if (
      attribute?.name?.toLowerCase() === 'assigneduser' &&
      pluginConfig?.HDFCUAM
    ) {
      return permissions?.is_super_admin || groupDetails?.isGroupAdmin;
    }
    return isUserAuthorizedForUpdateAttribute;
  }, [
    attribute?.name,
    permissions?.is_super_admin,
    groupDetails?.isGroupAdmin,
    pluginConfig?.HDFCUAM,
    isUserAuthorizedForUpdateAttribute,
  ]);

  // Create a memoized handler for the edit button click to avoid arrow function in JSX
  const handleEditClick = useCallback(() => {
    if (onEdit) {
      onEdit(attribute);
    }
  }, [onEdit, attribute]);

  const openChatBox = useCallback(() => {
    handleOpenChat(allAttributeChats?.[attribute?.name], attribute);
  }, [allAttributeChats, attribute?.name, handleOpenChat]);

  const handleClick = useCallback(() => {
    handleAttributeClick(attribute);
  }, [attribute, handleAttributeClick]);

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const showCalculationIcon = useMemo(() => {
    return (
      isDerived &&
      !isEditable &&
      attribute?.input_control_type !== 'SINGLE_SELECT' &&
      attribute?.data_type !== 'InventoryView' &&
      !frontendConfig?.hideSingleEditMode
    );
  }, [
    isDerived,
    isEditable,
    attribute?.input_control_type,
    frontendConfig?.hideSingleEditMode,
  ]);

  //show the history icon
  const showHistoryIcon = useMemo(() => {
    return attribute?.show_history;
  }, [attribute]);
  const handleHistoryClick = useCallback(
    (e) => {
      e.stopPropagation();
      onHistoryClick(attribute);
    },
    [onHistoryClick, attribute]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      px={1}
      sx={{
        '&:hover .edit-icon-button': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <Box
        display="inline-flex"
        alignItems="center"
        maxWidth="100%"
        minWidth={0}
      >
        <Tooltip
          title={typeof label !== 'object' && isLabelOverflowed ? label : ''}
        >
          <Typography
            ref={labelRef}
            variant="body4"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              maxWidth: '100%',
              ':hover': {
                fontWeight: 600,
                cursor: 'pointer',
              },
            }}
            onClick={handleClick}
          >
            {label}
          </Typography>
        </Tooltip>

        {isRequired && (
          <Typography
            component="span"
            variant="body8"
            color="error.main"
            sx={{
              marginLeft: '2px',
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            *
          </Typography>
        )}

        {showHistoryIcon && (
          <Tooltip title="History">
            <IconButton
              size="small"
              color="primary"
              onClick={handleHistoryClick}
              aria-label={`View history for ${label}`}
              sx={{ flexShrink: 0, ml: 1 }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '36px',
        }}
      >
        <Tooltip
          title={
            typeof displayValue !== 'object' && isValueOverflowed
              ? displayValue
              : ''
          }
        >
          <Typography
            ref={valueRef}
            variant="body8"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
            }}
          >
            {displayValue}
            {matchedOverride?.justification && (
              <OverrideRationaleTooltip
                justification={matchedOverride?.justification}
              />
            )}
          </Typography>
        </Tooltip>
        {isEditEnabled && (
          <IconButton
            className="edit-icon-button"
            onClick={handleEditClick}
            size="small"
            color="primary"
            title={
              isUserEditEnabled ? 'Edit' : NOT_AUTHORIZED_TO_ACCESS_MESSAGE
            }
            aria-label={
              isUserEditEnabled ? 'Edit' : NOT_AUTHORIZED_TO_ACCESS_MESSAGE
            }
            disabled={!isUserEditEnabled}
            sx={{
              flexShrink: 0,
              opacity: 0,
              visibility: 'hidden',
              transition:
                'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <ModeEditOutlinedIcon />
          </IconButton>
        )}
        {showCalculationIcon && (
          <IconButton
            className="edit-icon-button disabled-svg-icon"
            onClick={handleClickAttribute}
            size="small"
            color="primary"
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
            disabled={!attribute?.show_calculation_popUp}
            aria-label="Calculate Derived Attribute"
            sx={{
              flexShrink: 0,
              opacity: 0,
              visibility: 'hidden',
              transition:
                'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            }}
          >
            <CalculationIcon />
          </IconButton>
        )}

        {!isValueEmpty(allAttributeChats?.[attribute?.name]) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={openChatBox}
          >
            <Badge
              badgeContent={allAttributeChats?.[attribute?.name]?.length}
              sx={{
                '& .MuiBadge-badge': {
                  marginRight: '0px',
                  marginTop: '0px',
                },
              }}
            >
              <ChatIcon color="primary" />
            </Badge>
          </Box>
        )}
      </Box>
    </Box>
  );
};

FieldInfo.propTypes = {
  label: PropTypes.string.isRequired,
  hasDraftChanges: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.object,
  ]),
  onEdit: PropTypes.func,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    data_type: PropTypes.string,
    input_control_type: PropTypes.string,
    description: PropTypes.string,
    is_editable: PropTypes.bool,
    is_derived: PropTypes.bool,
    show_history: PropTypes.bool,
    show_calculation_popUp: PropTypes.bool,
  }),
  groupDetails: PropTypes.shape({
    isGroupAdmin: PropTypes.bool,
    userName: PropTypes.string,
    userId: PropTypes.number,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        userId: PropTypes.number,
        username: PropTypes.string,
      })
    ),
  }),
  mode: PropTypes.oneOf(['edit', 'view']),
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  handleOpenDerivedModal: PropTypes.func.isRequired,
  overrideData: PropTypes.oneOf([PropTypes.object]).isRequired,
  entityId: PropTypes.number,
  entityDetailsFormikForm: PropTypes.oneOf([PropTypes.object]).isRequired,
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  entityType: PropTypes.string,
  onHistoryClick: PropTypes.func,
  associationTypes: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        entity_name: PropTypes.string,
        entity_type: PropTypes.string,
        template_id: PropTypes.string,
        version_id: PropTypes.string,
      })
    )
  ),
  entityData: PropTypes.shape({}),
};

export default React.memo(FieldInfo);
